// "Use these specs" (2026-09-03, Michael's staff request; admin-only to start).
//
// Building a cart with several pieces that share one spec (same paper, mat,
// frame, glass…) meant rebuilding the whole visualizer flow per image, because
// picking an image is a full page load and the visualizer state is in memory.
//
// A cart line's "Use these specs" button snapshots that line's specs here
// (localStorage), sends the user to Uploads to pick the next image, and the
// visualizer applies the snapshot ON THAT NEXT LOAD ONLY (one-shot: consumed
// when applied, so a stale template never surprises anyone later).
//
// It reuses the cart-Edit restore machinery: the same `options` shape goes
// into the store, and the option panels' edit-mode signal sync is gated on
// `editingItemId || seededSpecs`. Size is NOT carried — it belongs to the new
// image (perfect-fit / custom), so the user still confirms size.
import { selectHdAlu } from "@/stores/actions/selectHdAlu";
import { $visualizer, visualizerService } from "@/stores/visualizer";
import {
  PRODUCT_TYPE,
  PRODUCT_TYPE_PROPS,
  ProductType,
} from "@/utils/content/productTypes";
import type { LineItem } from "@/utils/types/types";
import { mountOnlyFromItemJson } from "@/stores/actions/restoreVisualizerState";
import { mountSubstrateName } from "@/utils/content/mountOnly";

export type SpecTemplate = {
  productType: string; // itemJson.productType ("Framed Print", "Canvas", …)
  options: any; // visualizer store `options` (display/frame/paper/treatment/finish)
  label: string; // human cue for the toast
  savedAt: string;
};

// Plain localStorage, NOT a nanostores persistent atom: a persistent atom only
// rehydrates once it has a subscriber, and after the hard navigation into
// /visualizer nothing subscribes before the mount effect reads it — so .get()
// returns the initial null (same trap the shared-cart session hit; see the
// comment in Visualizer.tsx). Verified on staging 2026-09-03: the button
// saved, the visualizer read null, specs never applied.
const KEY = "specTemplate";

function readTemplate(): SpecTemplate | null {
  try {
    const raw = window.localStorage.getItem(KEY);
    return raw ? (JSON.parse(raw) as SpecTemplate) : null;
  } catch {
    return null;
  }
}
function writeTemplate(tpl: SpecTemplate | null): void {
  try {
    if (tpl) window.localStorage.setItem(KEY, JSON.stringify(tpl));
    else window.localStorage.removeItem(KEY);
  } catch {
    /* storage unavailable — feature silently no-ops */
  }
}

/** Snapshot a cart line's specs. Mirrors restoreVisualizerState's option mapping. */
export function saveSpecTemplate(lineItem: LineItem): void {
  const itemJson = (lineItem.itemJson || {}) as any;
  const productTypeLabel = String(itemJson.productType || "");
  const treatmentType = String(itemJson.treatmentOptions?.type || "");
  const hasFraming = productTypeLabel.match(/framed/giu)
    ? true
    : productTypeLabel.match(/fine art/giu)
      ? false
      : !!treatmentType.match(/(frame|floater|matted|shadow|float|straight)/giu);
  const cartData = itemJson.cartData || {};
  // Label = everything the template carries, so the banners say what will be
  // applied: paper, border + edge, mat, treatment, frame (or canvas/HD finish).
  const po = itemJson.paperOptions || {};
  const mb = itemJson.frameOptions?.matboard;
  const isGiclee = /fine art|framed/i.test(productTypeLabel);
  const parts: string[] = [];
  if (isGiclee) {
    parts.push(po.paper || cartData.staticField3);
    const border = Number(po.size) || 0;
    if (border > 0) {
      const edge = po.style === 2 || po.style === 3 ? "deckled" : "straight cut";
      parts.push(`${border}" border, ${edge}`);
    } else if (!/mat/i.test(treatmentType)) {
      parts.push("full bleed");
    }
    if (/mat/i.test(treatmentType) && mb?.size) {
      parts.push(`${mb.size}" ${mb.label || ""} ${mb.ply || ""} mat`.replace(/\s+/g, " ").trim());
    }
    const mountOnly = mountOnlyFromItemJson(itemJson);
    parts.push(
      treatmentType === "No Frame"
        ? "print only"
        : /mat/i.test(treatmentType) && itemJson.frameOptions?.matboard?.only
          ? "mat only"
          : mountOnly
            ? `mounted on ${mountSubstrateName(mountOnly.substrate)}${mountOnly.frame ? ` in ${mountOnly.frame}` : ""}${mountOnly.brace ? ", brace" : ""}${mountOnly.cleat ? ", cleat" : ""}`
            : treatmentType
    );
    if (hasFraming && itemJson.frameOptions?.label) parts.push(itemJson.frameOptions.label);
    if (hasFraming && itemJson.frameOptions?.glass?.type) parts.push(itemJson.frameOptions.glass.type);
    if (hasFraming && itemJson.frameOptions?.mount?.type) parts.push(`Mounted to ${itemJson.frameOptions.mount.type}`);
  } else {
    parts.push(cartData.staticField3 || po.type || itemJson.type, treatmentType);
    if (/float|frame/i.test(treatmentType) && itemJson.frameOptions?.label) {
      parts.push(itemJson.frameOptions.label);
    }
  }
  const label = parts.filter(Boolean).join(" · ");
  writeTemplate({
    productType: productTypeLabel,
    options: {
      display: hasFraming,
      frameOptions: itemJson.frameOptions || {},
      paperOptions: itemJson.paperOptions || {},
      treatmentOptions: itemJson.treatmentOptions || {},
      finish: itemJson.type || undefined,
      // Staff mount-only spec rides along (mountOnly.ts) — applied verbatim by the `...tpl.options` spread below.
      mountOnly: mountOnlyFromItemJson(itemJson),
    },
    label,
    savedAt: new Date().toISOString(),
  });
}

function toProductType(label: string): ProductType {
  if (/canvas/i.test(label)) return PRODUCT_TYPE.CANVAS;
  if (/aluminum/i.test(label)) return PRODUCT_TYPE.HD_ALUMINUM;
  return PRODUCT_TYPE.GICLEE;
}

/** Read without consuming — for the Uploads banner. */
export function peekSpecTemplate(): SpecTemplate | null {
  return readTemplate();
}
export function clearSpecTemplate(): void {
  writeTemplate(null);
}

/**
 * Apply and consume the pending template. Returns its label when applied,
 * null when there was nothing pending. Call from the visualizer's normal
 * (non-edit) mount path, in place of the default selectProduct(GICLEE).
 */
export function applySpecTemplate(): string | null {
  const tpl = readTemplate();
  if (!tpl) return null;
  writeTemplate(null); // one-shot
  const productType = toProductType(tpl.productType);
  const store = $visualizer.get();
  // Merge the line's specs OVER the product defaults. A print-only line
  // stores a zeroed frameOptions (no texture/label/glass — the 07-22
  // phantom-frame fix), and seeding that verbatim starves the 3D frame
  // pipeline → blank canvas (the 07-23 failure mode; staging 2026-09-03).
  // A fresh visualizer always carries the default frame in the store while
  // `display` is false, so this reproduces the normal state.
  const defaults: any = (PRODUCT_TYPE_PROPS as any)[productType]?.options || {};
  // Staff custom (special-order) moulding: the line's label is the custom text; seed the
  // stand-in frame's label so the frame renders (customMoulding.ts, same as restoreVisualizerState).
  const savedFrame = tpl.options?.frameOptions || {};
  const lineFrame = savedFrame.customMoulding?.standIn
    ? { ...savedFrame, label: savedFrame.customMoulding.standIn }
    : savedFrame;
  const options = {
    ...defaults,
    ...tpl.options,
    // Framed: the line's frame. Unframed: the saved frame is zeroed (face 0 /
    // depth 0 — degenerate geometry), so use the default hidden frame and keep
    // only the line's matboard.
    frameOptions: tpl.options?.display
      ? { ...defaults.frameOptions, ...lineFrame }
      : {
          ...defaults.frameOptions,
          ...(lineFrame.matboard ? { matboard: lineFrame.matboard } : {}),
        },
    paperOptions: { ...defaults.paperOptions, ...(tpl.options?.paperOptions || {}) },
    treatmentOptions: {
      ...defaults.treatmentOptions,
      ...(tpl.options?.treatmentOptions || {}),
    },
  };
  $visualizer.set({
    ...store,
    selectedProduct: productType,
    options,
    seededSpecs: true,
    seededLabel: tpl.label || "",
    forceUpdate: store.forceUpdate + 1,
  });
  visualizerService.selectProduct(productType);
  if (productType === PRODUCT_TYPE.HD_ALUMINUM && tpl.options?.finish) {
    selectHdAlu({ finish: tpl.options.finish });
  }
  return tpl.label || "previous item";
}
